// src/store/menuSlice.js

import chroma from 'chroma-js';

import { showTemporaryHint } from '../utils/interface/showHint';
import { createBetterClick } from '../utils/interface/createBetterClick';


const createMenuSlice = (set, get, ref) => {

  // 1. Конфигурация допустимых параметров Telegram Haptic API
  const HAPTIC_CONFIG = {
    impactOccurred: ['light', 'medium', 'heavy', 'rigid', 'soft'],
    notificationOccurred: ['error', 'success', 'warning'],
    selectionChanged: [] // Без параметров
  };

  // 2. Безопасный исполнитель (Validation + Try/Catch)
  const executeSafeHaptic = (level, reason) => {
    try {
      const tgHaptic = window.Telegram?.WebApp?.HapticFeedback;
      if (!tgHaptic) return;

      const safeReason = HAPTIC_CONFIG.hasOwnProperty(reason) ? reason : 'impactOccurred';

      const allowedLevels = HAPTIC_CONFIG[safeReason];
      let safeLevel = level;

      if (allowedLevels.length > 0 && !allowedLevels.includes(safeLevel)) {
        safeLevel = allowedLevels[0];
      }

      // Вызов метода
      if (typeof tgHaptic[safeReason] === 'function') {
        if (allowedLevels.length > 0) {
          tgHaptic[safeReason](safeLevel);
        } else {
          tgHaptic[safeReason](); // Для selectionChanged
        }
      }
    } catch (error) {
      console.warn('Haptic feedback error:', error);
    }
  };

  ref.menu = {
    params: {},
    opened: {},
    panel: {
      offset: 400,
      fastColorIndicatorY: 445,
    },
    source: {
      isOpen: false,
      imageUrl: '',
      aspectRatio: 1,
      position: { x: 20, y: 20 },
      size: { width: 200, height: 120 }
    },
    activeTheme: 'cardcolor',
    activeHintId: null, 
  }

  ref.menu.methods = {
    showTemporaryHint,
    createBetterClick,

    forceRender: ()=>{
      set(state=>({render: state.render + 1, activeHintId: ref.menu.activeHintId}))
    },
    render: ()=>{
      set(state=>({render: state.render + 1, activeHintId: ref.menu.activeHintId}))
    },
    showPopupMessage: (message) =>{
      set({popupMessage: message});
      if (message && !message.mute) { 
        const level = message.haptic?.level || 'warning';
        const reason = message.haptic?.reason || 'notificationOccurred';
        ref.menu.methods.appleHaptic(level, reason);
      }
      ref.menu.methods.render();
    },
    openMenu: (menuId, more = {}) =>{
      ref.menu.params = more;
      ref.menu.opened[menuId] = more;
      set(state=>{
        if (more.layer) {
          return { showMenu: [menuId, ...(state.showMenu.filter(currentId => currentId !== menuId))] };
        } else {
          return { showMenu: [menuId] }
        }
      })
    },

    toggleMenu: (menuId) =>{
      set(state => ({showMenu: state.showMenu?.[0] === menuId ? [] : [menuId]}))
    },

    closeMenu: (menuId) =>{
      set(state=>({showMenu: menuId ? state.showMenu.filter(currentId => currentId !== menuId) : []}))
    },


    delayPremiumMenu: (value, delay = 50)=>{
      setTimeout(() => {
        set({showPremiumMenu: value})
      }, delay);
    },

    adjustColor: (cssVariable, baseColor) => {
      function getCSSVariableValue(variable) {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
      }

      const cssColor = getCSSVariableValue(cssVariable);
      const cssChroma = chroma(cssColor);
      const baseChroma = chroma(baseColor);
      const [, s, l] = cssChroma.hsl();
      const [h, , ] = baseChroma.hsl();
      const resultColor = chroma.hsl(h, s, l);
      return resultColor.css();
    },

     blendColors: (cssVariable, baseColor, opacity = 0.2) => {
      function getCSSVariableValue(variable) {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
      }
      const cssColor = getCSSVariableValue(cssVariable);
      const cssChroma = chroma(cssColor);
      const baseChroma = chroma(baseColor);
      const blendedColor = chroma.mix(cssChroma, baseChroma, opacity, 'rgb');
      return blendedColor.css();
    },

    // 3. Обновленные методы с сохранением проверок устройства
    appleHaptic: (level = 'light', reason = 'impactOccurred') => {
      if (!ref.info?.user?.vibro) return;
      if (ref.info.methods.isAppleDevice()) {
        executeSafeHaptic(level, reason);
      }
    },

    androidHaptic: (level = 'success', reason = 'notificationOccurred') => {
      if (!ref.info?.user?.vibro) return;
      if (!ref.info.methods.isAppleDevice()) {
        executeSafeHaptic(level, reason);
      }
    },

    // Метод для активации подсказки
    triggerHint: (id) => {
      const info = ref.info;
      // Проверяем, была ли подсказка просмотрена ранее
      const hasBeenSeen = info.user?.hints?.[id];

      if (!hasBeenSeen && !ref.menu.activeHintId) {
        ref.menu.activeHintId = id;
        ref.menu.methods.render();
      }
    },

    // Метод для закрытия подсказки
    closeHint: (id, markAsRead = true) => {
      if (markAsRead) {
        // Записываем на сервер и в локальный стейт
        ref.work.methods.saveUserSets({ [`hints.${id}`]: Date.now() });

        if (!ref.info.user.hints) ref.info.user.hints = {};
        ref.info.user.hints[id] = Date.now();
      }

      ref.menu.activeHintId = null;
      ref.menu.methods.render();
    },

    setTheme: (themeName) => {
      ref.menu.activeTheme = themeName;
      ref.menu.methods.render();
    }

  }

  // 4. Глобальный метод: сам определяет контекст
  window.croco = window.croco || {}

  window.croco.haptic = (level, reason) => {
    if (ref.info.methods.isAppleDevice()) {
      ref.menu.methods.appleHaptic(level, reason);
    } else {
      ref.menu.methods.androidHaptic(level, reason);
    }
  }

  window.croco.appleHaptic = ref.menu.methods.appleHaptic;
  window.croco.androidHaptic = ref.menu.methods.androidHaptic;

  return {
    render: 0,
    spinner: 0,
    showMenu: [],
    showModeHelp: true,
    // Выносим в плоскую структуру для быстрой подписки в React
    activeHintId: null,


  }

};

export default createMenuSlice;
